import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Activity, Mail, Lock, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      navigate('/workspace');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen auth-bg flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo — links back to landing */}
        <Link
          to="/"
          className="group flex items-center justify-center gap-2 mb-8 relative py-2 px-4 rounded-xl transition-all duration-300 hover:scale-[1.03] active:scale-[0.98]"
          data-testid="login-logo-link"
        >
          <span className="absolute inset-0 rounded-xl bg-[#2E79FF]/0 group-hover:bg-[#2E79FF]/8 transition-all duration-300" />
          <span className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-[0_0_20px_rgba(46,121,255,0.15)]" />
          <Activity className="w-8 h-8 text-[#2E79FF] transition-transform duration-300 group-hover:rotate-[-8deg] group-hover:scale-110 relative z-10" />
          <span className="text-2xl font-semibold tracking-tight relative z-10 bg-gradient-to-r from-white to-white bg-clip-text group-hover:from-[#2E79FF] group-hover:to-[#60a5fa] group-hover:text-transparent transition-all duration-300">MSML HEALTH</span>
        </Link>

        {/* Login Card */}
        <div className="msml-card p-8" data-testid="login-form">
          <div className="text-center mb-8">
            <h1 className="text-xl font-medium text-[var(--msml-text-primary)] mb-2">Welcome back</h1>
            <p className="text-sm text-[var(--msml-text-secondary)]">Sign in to continue to your workspace</p>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-[var(--msml-danger-bg)] border border-[var(--msml-danger)]/20 mb-6" data-testid="login-error">
              <AlertCircle className="w-4 h-4 text-[var(--msml-danger)]" />
              <span className="text-sm text-[var(--msml-danger)]">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm text-[var(--msml-text-secondary)]">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--msml-text-muted)]" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="msml-input pl-10"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm text-[var(--msml-text-secondary)]">Password</Label>
                <Link to="/forgot-password" className="text-xs text-[var(--msml-primary)] hover:underline">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--msml-text-muted)]" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="msml-input pl-10"
                  required
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="msml-btn-primary w-full"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <span className="msml-spinner" />
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[var(--msml-border)]"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-2 bg-[var(--msml-panel)] text-[var(--msml-text-muted)]">or continue with</span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="msml-btn-secondary w-full"
            onClick={loginWithGoogle}
            data-testid="login-google-btn"
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </Button>

          <p className="text-center text-sm text-[var(--msml-text-secondary)] mt-6">
            Don't have an account?{' '}
            <Link to="/signup" className="text-[var(--msml-primary)] hover:underline" data-testid="signup-link">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
